import { DeleteOutlined, ExportOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Divider, Input, Tooltip, Typography, message } from 'antd';
import axios from 'axios';
import React, { useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBuilderContext } from '..';

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * Renders the PaymentMethodEditor component.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.currentQuestion - The current question object.
 * @param {import('react-hook-form').UseFieldArrayReturn<FieldValues, "merchants", "id">} props.fieldArrayProps - The field array props.
 * @param {Function} props.update - The update function.
 * @returns {JSX.Element|JSX.Element[]} The PaymentMethodEditor component.
 */
const PaymentMethodEditor = ({ currentQuestion, fieldArrayProps, update }) => {
  const { merchants } = currentQuestion || {};

  const { fields } = fieldArrayProps;

  if (!merchants) return <></>;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {fields?.map((merchant, index) => (
        <MerchantEditor
          key={merchant?.type}
          field={merchant}
          index={index}
          update={update}
          currentQuestion={currentQuestion}
        />
      ))}
    </div>
  );
};

const MerchantEditor = ({ field, index, update, currentQuestion }) => {
  const { t } = useTranslation(['builder']);
  const { control, getValues } = useFormContext();
  const { form } = useBuilderContext();
  const [connectStripeLoading, setConnectStripeLoading] = useState(false);
  const [disconnectStripeLoading, setdisconnectStripeLoading] = useState(false);

  const isStripeConnected = currentQuestion?.merchants?.some(
    (merchant) =>
      merchant?.type === 'stripe' &&
      !!merchant?.credentials?.accountId &&
      merchant?.credentials?.detailsSubmitted,
  );

  const connectWithStripe = async () => {
    setConnectStripeLoading(true);
    axios
      .post('/api/merchants/stripe/onboard-user', {
        formId: form?.id,
        questionId: currentQuestion?.questionId,
      })
      .then((res) => {
        const { data } = res || {};
        window.location.href = data.url;
      })
      .catch(() => {
        setConnectStripeLoading(false);
        message.error('Failed to connect with stripe. Please try again.');
      });
  };

  const removeMerchant = async (merchantType) => {
    const newMerchants = currentQuestion?.merchants?.filter(
      (merchant) => merchant?.type !== merchantType,
    );
    update({ merchants: newMerchants });
  };

  const disconnectWithStripe = async () => {
    setdisconnectStripeLoading(true);
    const res = await axios
      .post('/api/merchants/stripe/disconnect', {
        formId: form?.id,
        questionId: currentQuestion?.questionId,
      })
      .then(() => {
        const newMerchants = currentQuestion?.merchants?.filter(
          (merchant) => merchant?.type !== 'stripe',
        );
        update({ merchants: newMerchants });
      })
      .catch(() => {
        setdisconnectStripeLoading(false);
        message.error('Failed to disconnect with stripe. Please try again.');
      });
  };

  let view = <></>;

  const merchantChangeHandler = () => {
    const currentMerchants = getValues('merchants');
    update({ merchants: currentMerchants });
  };

  switch (field.type) {
    case 'paypal':
      view = (
        <div
          style={{
            borderRadius: 5,
            border: '1px solid #d9d9d9',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 16px',
              paddingRight: '8px',
              backgroundColor: '#f7f7f7',
              borderBottom: 'inherit',
              // borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
            }}
          >
            <Typography.Text strong>PayPal</Typography.Text>
            <Button
              icon={<DeleteOutlined style={{ color: 'dimgray' }} />}
              type="text"
              danger
              title="delete"
              size="small"
              onClick={() => removeMerchant('paypal')}
            />
          </div>
          {/* <Divider style={{ margin: '8px 0' }} /> */}
          <div className="form-group" style={{ padding: '12px 16px' }}>
            <label className="form-group-label" style={{ fontWeight: 400 }}>
              {t('builder:propertiesPanel.payment.paypal.email')}
              {/* <Tooltip placement="top" title={t('builder:propertiesPanel.section.labelTooltip')}>
                <QuestionCircleOutlined />
              </Tooltip> */}
            </label>
            <Controller
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  size="large"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!invalid) merchantChangeHandler();
                  }}
                />
              )}
              name={`merchants.${index}.credentials.email`}
              rules={{
                pattern: {
                  value: emailRegex,
                  message: 'Invalid email address',
                },
              }}
              control={control}
            />
            {/* <label className="form-group-label" style={{ fontWeight: 400, marginTop: '12px' }}>
              {t('builder:propertiesPanel.payment.paypal.label', 'Label')}
            </label> */}
            {/* <Controller
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  size="large"
                  {...field}
                  showCount
                  maxLength={20}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!invalid) merchantChangeHandler();
                  }}
                />
              )}
              name={`merchants.${index}.credentials.label`}
              control={control}
              defaultValue={'Paypal'}
            /> */}
          </div>
        </div>
      );
      break;
    case 'stripe':
      view = (
        <div
          style={{
            borderRadius: 5,
            border: '1px solid #d9d9d9',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 16px',
              paddingRight: '8px',
              backgroundColor: '#f7f7f7',
              borderBottom: 'inherit',
              // borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
            }}
          >
            <Typography.Text strong>Stripe</Typography.Text>
            <Button
              icon={<DeleteOutlined />}
              type="text"
              title="delete"
              size="small"
              danger
              style={{ color: 'dimgray' }}
              onClick={disconnectWithStripe}
              loading={disconnectStripeLoading}
            />
          </div>
          <div style={{ padding: '24px 16px' }}>
            {isStripeConnected ? (
              <>
                <Typography.Text>
                  Connected successfully. <br />
                  <Typography.Link
                    href="https://dashboard.stripe.com/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Manage account on Stripe <ExportOutlined />
                  </Typography.Link>
                </Typography.Text>
                <div
                  className="form-group"
                  style={{ marginTop: '12px', marginBottom: '0', textAlign: 'left' }}
                >
                  <label className="form-group-label" style={{ fontWeight: 400 }}>
                    {t('builder:propertiesPanel.payment.cash.label')}
                  </label>
                  <Controller
                    render={({ field, fieldState: { invalid } }) => (
                      <Input
                        size="large"
                        {...field}
                        showCount
                        maxLength={20}
                        onChange={(e) => {
                          field.onChange(e);
                          if (!invalid) merchantChangeHandler();
                        }}
                      />
                    )}
                    name={`merchants.${index}.credentials.label`}
                    control={control}
                    defaultValue={'Card'}
                  />
                </div>
              </>
            ) : (
              <Button
                type="text"
                onClick={connectWithStripe}
                loading={connectStripeLoading}
                size="large"
                block
                className="stripe-connect"
                style={{ backgroundColor: '#6772e5', color: '#fff' }}
              >
                <span>{t('builder:propertiesPanel.payment.connectWith')}</span>
                <a
                  href="https://stripe.com/"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    visibility: 'hidden',
                    // marginLeft: 6,
                    pointerEvents: 'none',
                  }}
                >
                  stripe
                </a>
              </Button>
            )}
          </div>
        </div>
      );
      break;
    case 'razorpay':
      view = (
        <div
          style={{
            borderRadius: 5,
            border: '1px solid #d9d9d9',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 16px',
              paddingRight: '8px',
              backgroundColor: '#f7f7f7',
              borderBottom: 'inherit',
              // borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
            }}
          >
            <Typography.Text strong>Razorpay</Typography.Text>
            <Button
              icon={<DeleteOutlined style={{ color: 'dimgray' }} />}
              type="text"
              danger
              title="delete"
              size="small"
              onClick={() => removeMerchant('razorpay')}
            />
          </div>
          {/* <Divider style={{ margin: '8px 0' }} /> */}
          <div className="form-group" style={{ padding: '12px 16px' }}>
            <label className="form-group-label" style={{ fontWeight: 400 }}>
              {t('builder:propertiesPanel.payment.razorpay.keyId', 'Key Id')}
            </label>
            <Controller
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  size="large"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!invalid) merchantChangeHandler();
                  }}
                />
              )}
              name={`merchants.${index}.credentials.keyId`}
              control={control}
            />
            <label className="form-group-label" style={{ fontWeight: 400 }}>
              {t('builder:propertiesPanel.payment.razorpay.keySecret', 'Key Secret')}
            </label>
            <Controller
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  size="large"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!invalid) merchantChangeHandler();
                  }}
                />
              )}
              name={`merchants.${index}.credentials.keySecret`}
              control={control}
            />
          </div>
        </div>
      );
      break;
    case 'cash':
      view = (
        <div
          style={{
            borderRadius: 5,
            border: '1px solid #d9d9d9',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 16px',
              paddingRight: '8px',
              backgroundColor: '#f7f7f7',
              borderBottom: 'inherit',
              // borderBottom: '1px solid rgba(0, 0, 0, 0.5)',
            }}
          >
            <Typography.Text strong>Cash</Typography.Text>
            <Button
              icon={<DeleteOutlined style={{ color: 'dimgray' }} />}
              type="text"
              danger
              title="delete"
              size="small"
              onClick={() => removeMerchant('cash')}
            />
          </div>
          {/* <Divider style={{ margin: '8px 0' }} /> */}
          <div className="form-group" style={{ padding: '12px 16px' }}>
            <label className="form-group-label" style={{ fontWeight: 400 }}>
              {t('builder:propertiesPanel.payment.cash.label')}
              {/* <Tooltip placement="top" title={t('builder:propertiesPanel.section.labelTooltip')}>
                <QuestionCircleOutlined />
              </Tooltip> */}
            </label>
            <Controller
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  size="large"
                  {...field}
                  showCount
                  maxLength={20}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!invalid) merchantChangeHandler();
                  }}
                />
              )}
              name={`merchants.${index}.credentials.label`}
              control={control}
            />
          </div>
        </div>
      );
      break;
    default:
      break;
  }
  return view;
};

export default PaymentMethodEditor;
